// レシートをもらえなかった買い物を手で登録するシート
//
// 滞在の詳細から開いたときは visitId を受け取り、その滞在での買い物として場所まで確定させる。
// 時刻が分からなくても滞在で行動の記録に結びつくので、時刻は空欄のままでよい。
import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

import { FIELD_LABELS } from '../field-labels'
import { useCreateReceipt } from '../hooks/use-receipts'
import {
  emptyItemDraft,
  PAYMENT_METHODS,
  type ReceiptDraft,
  type ReceiptItemDraft,
  toCreateReceiptParams,
} from '../receipt-create-form'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 開いたときの入力値。開くたびにここから始める */
  initialDraft: ReceiptDraft
  /** この滞在での買い物として登録する */
  visitId?: number
  /** どこでの買い物として登録するか（滞在の場所名など） */
  contextLabel?: string
  /** 滞在を指定しないときの場所探しで Google まで引くか（既定は引く） */
  useGoogle?: boolean
  onCreated?: (receiptId: number) => void
}

export const ReceiptCreateSheet = ({ open, onOpenChange, onCreated, ...formProps }: Props) => (
  <BottomSheet title="レシートを手で登録" open={open} onOpenChange={onOpenChange} snapPoints={[0.92]}>
    <ReceiptCreateForm
      {...formProps}
      onCreated={(receiptId) => {
        onOpenChange(false)
        onCreated?.(receiptId)
      }}
    />
  </BottomSheet>
)

type FormProps = Pick<Props, 'initialDraft' | 'visitId' | 'contextLabel' | 'useGoogle'> & {
  onCreated: (receiptId: number) => void
}

const ReceiptCreateForm = ({ initialDraft, visitId, contextLabel, useGoogle, onCreated }: FormProps) => {
  const [draft, setDraft] = useState(initialDraft)
  const createReceipt = useCreateReceipt()

  const update = (patch: Partial<ReceiptDraft>) => setDraft((current) => ({ ...current, ...patch }))

  const updateItem = (index: number, patch: Partial<ReceiptItemDraft>) =>
    setDraft((current) => ({
      ...current,
      items: current.items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }))

  const removeItem = (index: number) =>
    setDraft((current) => ({ ...current, items: current.items.filter((_, i) => i !== index) }))

  const canSubmit = draft.store_name.trim() !== '' && draft.date !== '' && !createReceipt.isPending

  const handleSubmit = () => {
    createReceipt.mutate(toCreateReceiptParams(draft, visitId, { useGoogle }), {
      onSuccess: (receipt) => {
        toast.success('レシートを登録しました。')
        onCreated(receipt.id)
      },
      onError: () => toast.error('登録に失敗しました。'),
    })
  }

  return (
    <div className="flex flex-col gap-4 px-4 pb-6">
      {contextLabel && <p className="text-xs text-muted-foreground">{contextLabel} での買い物として登録します</p>}

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{FIELD_LABELS.store_name}</span>
        <Input value={draft.store_name} onChange={(e) => update({ store_name: e.target.value })} />
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{FIELD_LABELS.purchased_date}</span>
          <Input type="date" value={draft.date} onChange={(e) => update({ date: e.target.value })} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{FIELD_LABELS.purchased_time}（分からなければ空欄）</span>
          <Input type="time" value={draft.time} onChange={(e) => update({ time: e.target.value })} />
        </label>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{FIELD_LABELS.total_amount}（空欄なら明細の合計）</span>
        <Input
          inputMode="numeric"
          value={draft.total_amount}
          onChange={(e) => update({ total_amount: e.target.value })}
        />
      </label>

      <div className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{FIELD_LABELS.payment_method}</span>
        <div className="flex flex-wrap gap-1.5">
          {PAYMENT_METHODS.map((method) => (
            <Button
              key={method}
              type="button"
              size="sm"
              variant={draft.payment_method === method ? 'default' : 'outline'}
              aria-pressed={draft.payment_method === method}
              onClick={() => update({ payment_method: draft.payment_method === method ? '' : method })}
            >
              {method}
            </Button>
          ))}
        </div>
      </div>

      <section className="flex flex-col gap-2">
        <h3 className="text-sm text-muted-foreground">明細（任意）</h3>
        {draft.items.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              aria-label={`品名 ${index + 1}`}
              placeholder="品名"
              value={item.name}
              onChange={(e) => updateItem(index, { name: e.target.value })}
              className="flex-1"
            />
            <Input
              aria-label={`金額 ${index + 1}`}
              placeholder="金額"
              inputMode="numeric"
              value={item.amount}
              onChange={(e) => updateItem(index, { amount: e.target.value })}
              className="w-24"
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label={`明細 ${index + 1} を削除`}
              onClick={() => removeItem(index)}
            >
              <X className="size-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="self-start gap-1"
          onClick={() => update({ items: [...draft.items, emptyItemDraft()] })}
        >
          <Plus className="size-4" />
          明細を追加
        </Button>
      </section>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{FIELD_LABELS.notes}</span>
        <Textarea value={draft.notes} onChange={(e) => update({ notes: e.target.value })} rows={2} />
      </label>

      <Button type="button" onClick={handleSubmit} disabled={!canSubmit} className="w-full">
        登録する
      </Button>
    </div>
  )
}
